import { Box, IconButton, Table, Tag, Text } from '@nimbus-ds/components';
import { EditIcon, TrashIcon } from '@nimbus-ds/icons';
import {
  ShippingAppResponseDto,
  ShippingRestrictionResponseDto,
} from '@tiendanube/common';
import { ShippingRestrictionScope } from '@tiendanube/common/src/enums';
import { Stack } from 'commons/components';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import Skeleton from './Skeleton';
import { getRestrictionTagLabel } from '../../utils/getRestrictionTagLabel';

interface ShippingRestrictionsTableProps {
  restrictions: ShippingRestrictionResponseDto[];
  detailActiveApp: ShippingAppResponseDto;
  onEdit: (restrictionId: string) => void;
  onDelete: (restrictionId: string) => void;
}

function ShippingRestrictionsTable({
  restrictions,
  detailActiveApp,
  onEdit,
  onDelete,
}: Readonly<ShippingRestrictionsTableProps>): JSX.Element {
  const t = useTranslationShipping('deliveryMethods.shippingLimiting.listPage');

  if (restrictions.length === 0) {
    return (
      <Box padding="4" display="flex" justifyContent="center">
        <Text color="neutral-textLow">{t('table.emptyState')}</Text>
      </Box>
    );
  }

  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.Cell as="th">{t('table.shippingMethod')}</Table.Cell>
          <Table.Cell as="th">{t('table.activeRestrictions')}</Table.Cell>
          <Table.Cell as="th" width="10%">
            {t('table.actions')}
          </Table.Cell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {restrictions.map((restriction) => (
          <Table.Row key={restriction.id}>
            <Table.Cell>
              <Text>
                {restriction.shippingMethodsScope ===
                ShippingRestrictionScope.ALL
                  ? t('table.allShippingMethods')
                  : restriction.selectedShippingMethods &&
                    restriction.selectedShippingMethods.length > 0
                  ? restriction.selectedShippingMethods
                      .map((code) => detailActiveApp.options[code]?.optionName)
                      .filter((name) => name !== undefined)
                      .join(', ')
                  : ''}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <Stack spacing="tight" wrap>
                {restriction.activeTags.map((tag, index) => {
                  const labelKey = getRestrictionTagLabel({
                    restriction,
                    tagType: tag.type,
                  });
                  return (
                    <Tag key={`${tag.type}-${index}`}>
                      <Text>{t(labelKey)}</Text>
                    </Tag>
                  );
                })}
              </Stack>
            </Table.Cell>
            <Table.Cell>
              <Stack spacing="tight">
                <IconButton
                  size="2rem"
                  source={<EditIcon />}
                  onClick={() => onEdit(restriction.id)}
                  backgroundColor="neutral-surface"
                  aria-label={t('table.edit')}
                />
                <IconButton
                  size="2rem"
                  source={<TrashIcon />}
                  onClick={() => onDelete(restriction.id)}
                  backgroundColor="neutral-surface"
                  aria-label={t('table.delete')}
                />
              </Stack>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}

ShippingRestrictionsTable.Skeleton = Skeleton;

export default ShippingRestrictionsTable;
